export const detachedBadges = new Map()
let layoutScheduled = false

/**
 * Decide if a badge should be visually detached (positioned absolutely) rather than inline.
 * Missing or alt labels often need their own space; also buttons / overflow hidden ancestors.
 */
export function needsDetached(wrapper, isMissing) {
  if (isMissing) return true
  if (wrapper.closest('button, [role=button], .btn, .Button')) return true
  if (wrapper.classList.contains('i18n-key-alt')) return true
  const parent = wrapper.parentElement
  return !!(parent && getComputedStyle(parent).overflow !== 'visible')
}

/**
 * createDetachedBadge(wrapper, { label, missing?, alt?, hardCoded? })
 * Adds a floating badge for an annotated i18n wrapper element.
 */
export function createDetachedBadge(wrapper, config) {
  if (!wrapper || detachedBadges.has(wrapper)) return
  const { label, missing = false, alt = false, hardCoded = false, ok = false } = config || {}
  if (!label) return

  assignWrapperMeta(wrapper, { missing, alt, hardCoded, ok })
  const badge = buildBadgeElement({ label, missing, alt, hardCoded, ok })
  document.body.appendChild(badge)
  wrapper.setAttribute('data-i18n-detached', '1')
  detachedBadges.set(wrapper, badge)
  scheduleBadgeLayout()
}

function assignWrapperMeta(wrapper, { missing, alt, hardCoded, ok }) {
  if (!wrapper.dataset.i18nType) {
    if (missing) wrapper.dataset.i18nType = 'missing'
    else if (alt) wrapper.dataset.i18nType = ok ? 'alt-ok' : 'alt'
    else wrapper.dataset.i18nType = 'key'
  }
  if (hardCoded) wrapper.dataset.i18nHardCoded = '1'
}

function buildBadgeElement({ label, missing, alt, hardCoded, ok }) {
  const badge = document.createElement('div')
  badge.className = 'i18n-key-badge'
  badge.textContent = label
  if (missing) badge.classList.add('missing')
  if (alt) badge.classList.add('alt')
  if (alt && ok) badge.classList.add('ok')
  if (alt && hardCoded) badge.classList.add('hard-coded')
  return badge
}

export function scheduleBadgeLayout() {
  if (layoutScheduled) return
  layoutScheduled = true
  requestAnimationFrame(() => {
    layoutScheduled = false
    detachedBadges.forEach((badge, wrapper) => {
      if (!document.body.contains(wrapper)) {
        badge.remove()
        detachedBadges.delete(wrapper)
        return
      }
      const rect = wrapper.getBoundingClientRect()
      const top = rect.top + window.scrollY - badge.offsetHeight - 2
      const left = rect.left + window.scrollX
      badge.style.top = `${Math.max(0, top)}px`
      badge.style.left = `${left}px`
    })
  })
}

export function initBadgeGlobalListeners() {
  window.addEventListener('scroll', scheduleBadgeLayout, { passive: true })
  window.addEventListener('resize', scheduleBadgeLayout)
}
